import { useMutation } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { reorderScheduledDrivesMutation, rescheduleDriveMutation, getRequirementSubintervalsQueryKey } from '@/api/main'
import {
  RequirementSubintervalsResponse,
  DriveSubinterval,
  ScheduledDriveResponseWithEndpoints,
  BillingPeriodResponse,
} from '@/api/main/types.gen'
import { queryClient } from '@/lib/queryClient'
import { produce } from 'immer'
import { sortBy } from 'lodash-es'

interface DriveIntervalsQueryKey {
  path: {
    billing_period_id: number
    starting_from: string
    ending_at: string
  }
}

interface UseSchedulerMutationsOptions {
  overlappingPeriods?: BillingPeriodResponse[]
}

export function useReorderDrives(
  driveIntervalsQueryKey: DriveIntervalsQueryKey,
  options?: UseSchedulerMutationsOptions
) {
  const { overlappingPeriods } = options || {}

  return useMutation({
    ...reorderScheduledDrivesMutation(),
    onMutate: data => {
      const queryKey = getRequirementSubintervalsQueryKey(driveIntervalsQueryKey)
      queryClient.cancelQueries({ queryKey })

      const previousScheduledDrives = queryClient.getQueryData(queryKey) as RequirementSubintervalsResponse | undefined

      if (previousScheduledDrives) {
        const index = previousScheduledDrives.drive_subintervals.findIndex(
          (it: { vehicle_id: string; interval: { starting_from: string; ending_at: string } }) =>
            it.vehicle_id === data.body.vehicle_id &&
            it.interval.starting_from === data.body.interval_start &&
            it.interval.ending_at === data.body.interval_end
        )

        if (index !== undefined && index !== -1) {
          const newData = produce(previousScheduledDrives, (draftState: RequirementSubintervalsResponse) => {
            draftState.drive_subintervals[index].drives = sortBy(
              draftState.drive_subintervals[index].drives,
              (drive: ScheduledDriveResponseWithEndpoints) => data.body.drive_ids.indexOf(drive.id)
            )
          })

          queryClient.setQueryData(queryKey, newData)
        }
      }

      return { previousScheduledDrives }
    },
    onSuccess: () => {
      showNotification({ message: 'Drives reordered' })
    },
    onError: (_error, _variables, context) => {
      if (context?.previousScheduledDrives) {
        const queryKey = getRequirementSubintervalsQueryKey(driveIntervalsQueryKey)
        queryClient.setQueryData(queryKey, context.previousScheduledDrives)
      }
      showNotification({ message: 'Error reordering drives', color: 'red' })
    },
    onSettled: () => {
      if (overlappingPeriods && overlappingPeriods.length > 0) {
        // Invalidate all requirementSubintervals queries for overlapping periods
        overlappingPeriods.forEach(period => {
          const queryKey = getRequirementSubintervalsQueryKey({
            path: {
              billing_period_id: period.id,
              starting_from: driveIntervalsQueryKey.path.starting_from,
              ending_at: driveIntervalsQueryKey.path.ending_at,
            },
          })
          queryClient.invalidateQueries({ queryKey })
        })
      } else {
        // Fall back to single period invalidation
        const queryKey = getRequirementSubintervalsQueryKey(driveIntervalsQueryKey)
        queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}

export function useRescheduleDrive(
  driveIntervalsQueryKey: DriveIntervalsQueryKey,
  options?: UseSchedulerMutationsOptions
) {
  const { overlappingPeriods } = options || {}

  return useMutation({
    ...rescheduleDriveMutation(),
    onMutate: ({ body }) => {
      const queryKey = getRequirementSubintervalsQueryKey(driveIntervalsQueryKey)
      queryClient.cancelQueries({ queryKey })

      const previousScheduledDrives = queryClient.getQueryData(queryKey) as RequirementSubintervalsResponse | undefined

      if (previousScheduledDrives) {
        const rescheduledDrive = previousScheduledDrives.drive_subintervals
          .flatMap((interval: DriveSubinterval) => interval.drives)
          .find((it: ScheduledDriveResponseWithEndpoints) => it.id === body.scheduled_drive_id)

        if (rescheduledDrive) {
          queryClient.setQueryData(
            queryKey,
            produce(previousScheduledDrives, (draft: RequirementSubintervalsResponse) => {
              // Remove the drive from the previous location
              draft.drive_subintervals.forEach((interval: DriveSubinterval) => {
                const index = interval.drives.findIndex(
                  (drive: ScheduledDriveResponseWithEndpoints) => drive.id === rescheduledDrive.id
                )
                if (index !== -1) {
                  interval.drives.splice(index, 1)
                }
              })

              // Insert the drive in the new location
              const targetInterval = draft.drive_subintervals.find(
                (it: DriveSubinterval) =>
                  it.vehicle_id === body.destination_vehicle_id &&
                  it.interval.starting_from === body.interval_start &&
                  it.interval.ending_at === body.interval_end
              )

              if (targetInterval) {
                if (body.schedule_before_drive !== undefined) {
                  // Insert before the specified drive
                  const index = targetInterval.drives.findIndex(
                    (drive: ScheduledDriveResponseWithEndpoints) => drive.id === body.schedule_before_drive
                  )
                  if (index >= 0) {
                    targetInterval.drives.splice(index, 0, rescheduledDrive)
                  } else {
                    // schedule_before_drive not found, push to beginning as fallback
                    targetInterval.drives.unshift(rescheduledDrive)
                  }
                } else if (body.schedule_after_drive !== undefined) {
                  const index = targetInterval.drives.findIndex(
                    (drive: ScheduledDriveResponseWithEndpoints) => drive.id === body.schedule_after_drive
                  )
                  if (index >= 0) {
                    targetInterval.drives.splice(index + 1, 0, rescheduledDrive)
                  } else {
                    // schedule_after_drive not found, push to end as fallback
                    targetInterval.drives.push(rescheduledDrive)
                  }
                } else {
                  // No positioning specified - push to end
                  targetInterval.drives.push(rescheduledDrive)
                }
              } else {
                // Create new interval if it doesn't exist
                draft.drive_subintervals.push({
                  vehicle_id: body.destination_vehicle_id,
                  interval: {
                    starting_from: body.interval_start,
                    ending_at: body.interval_end,
                  },
                  drives: [rescheduledDrive],
                  requirements: [],
                })
              }
            })
          )
        }
      }

      return { previousScheduledDrives }
    },
    onSuccess: () => {
      showNotification({ message: 'Drive rescheduled' })
    },
    onError: (_error, _variables, context) => {
      if (context?.previousScheduledDrives) {
        const queryKey = getRequirementSubintervalsQueryKey(driveIntervalsQueryKey)
        queryClient.setQueryData(queryKey, context.previousScheduledDrives)
      }
      showNotification({ message: 'Error rescheduling drive', color: 'red' })
    },
    onSettled: () => {
      if (overlappingPeriods && overlappingPeriods.length > 0) {
        // Invalidate all requirementSubintervals queries for overlapping periods
        overlappingPeriods.forEach(period => {
          const queryKey = getRequirementSubintervalsQueryKey({
            path: {
              billing_period_id: period.id,
              starting_from: driveIntervalsQueryKey.path.starting_from,
              ending_at: driveIntervalsQueryKey.path.ending_at,
            },
          })
          queryClient.invalidateQueries({ queryKey })
        })
      } else {
        // Fall back to single period invalidation
        const queryKey = getRequirementSubintervalsQueryKey(driveIntervalsQueryKey)
        queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}
